/**
 * Download a rendered PoDocument as a PDF (z8nrz7f7dz).
 *
 * Rasterises the SAME DOM the preview and Print show — html2canvas onto
 * jsPDF pages — so the PDF cannot drift from them. Both libraries are loaded
 * on demand: they are large, and only this button needs them.
 *
 * The element must be the preview-mode document, which carries its own 15 mm
 * padding; pages are sliced from it top to bottom and numbered "x / y".
 */
export async function downloadPoPdf(el: HTMLElement, fileName: string, paper: 'A4' | 'Letter') {
  const [{ jsPDF }, { default: html2canvas }] = await Promise.all([import('jspdf'), import('html2canvas')])
  const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false })

  const pdf = new jsPDF({ unit: 'mm', format: paper === 'Letter' ? 'letter' : 'a4', orientation: 'portrait' })
  const pageW = pdf.internal.pageSize.getWidth()
  const pageH = pdf.internal.pageSize.getHeight()
  // Canvas pixels per printed page height, at the page's full width.
  const slicePx = Math.floor((canvas.width * pageH) / pageW)
  const pages = Math.max(1, Math.ceil(canvas.height / slicePx))

  for (let i = 0; i < pages; i++) {
    const h = Math.min(slicePx, canvas.height - i * slicePx)
    const slice = document.createElement('canvas')
    slice.width = canvas.width
    slice.height = h
    const g = slice.getContext('2d')
    if (!g) throw new Error('Could not draw the PDF page.')
    g.fillStyle = '#ffffff'
    g.fillRect(0, 0, slice.width, h)
    g.drawImage(canvas, 0, i * slicePx, canvas.width, h, 0, 0, canvas.width, h)
    if (i > 0) pdf.addPage()
    pdf.addImage(slice.toDataURL('image/png'), 'PNG', 0, 0, pageW, (h * pageW) / canvas.width)
    pdf.setFontSize(8)
    pdf.setTextColor(107, 114, 128)
    pdf.text(`${i + 1} / ${pages}`, pageW - 15, pageH - 8, { align: 'right' })
  }

  pdf.save(fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`)
}
